import { useMemo, useState, useCallback, useRef, type FC } from "react";
import { PatchDiff, MultiFileDiff } from "@pierre/diffs/react";
import { type SelectedLineRange, type DiffLineAnnotation, type FileContents } from "@pierre/diffs";
import {
    convertHunksToPatch,
    computeContentHashForLines,
    computeContentHash,
    hashString,
    isLineInHunk,
    type PlainGitHunk,
} from "@/routes/environments/code-changes/diff-utils";
import { InlineCommentAnnotation } from "@/routes/environments/code-changes/InlineCommentAnnotation";
import type { ReviewComment } from "@/components/ai/review/types";
import "@/routes/environments/code-changes/themes/register";
import { useTheme } from "@/hooks/use-theme";
import { isTouchDevice } from "@/utils/is-touch-device";

export type LineSelection = {
    startLine: number;
    endLine: number;
    startIndex: number;
    endIndex: number;
    side?: "deletions" | "additions";
};

type AnnotationMetadata = {
    reviewComment?: ReviewComment;
    startLine: number;
    endLine: number;
};

export type GitDiffViewProps = {
    hunks: PlainGitHunk[];
    filePath?: string;
    onLineSelect?: (selection: LineSelection) => void;
    /** When true, shows all unchanged lines between hunks instead of collapsing them. */
    expandUnchanged?: boolean;
    comments?: ReviewComment[];
    onAddComment?: (
        content: string,
        startLine: number,
        endLine: number,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
    /** Send a single comment to Ona */
    onSendComment?: (reviewComment: ReviewComment) => void;
    /** Send all comments to Ona */
    onSendAllComments?: () => void;
    /** Total number of sendable comments */
    allCommentsCount?: number;
    /** Whether send actions are disabled */
    isSendDisabled?: boolean;
};

const GitDiffView: FC<GitDiffViewProps> = ({
    hunks,
    filePath = "file",
    expandUnchanged: expandUnchangedProp = false,
    comments,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onSetCommentThumbsUp,
    onSetCommentThumbsDown,
    onSendComment,
    onSendAllComments,
    allCommentsCount,
    isSendDisabled,
}) => {
    const [commentInputAnnotation, setCommentInputAnnotation] = useState<DiffLineAnnotation<AnnotationMetadata> | null>(
        null,
    );

    // For touch devices: track pending selection that needs a second tap to confirm
    // pending touch selection stores the initial selection
    const [pendingTouchSelection, setPendingTouchSelection] = useState<SelectedLineRange | null>(null);
    // final touch selection stores the final selection line range after the 2-tap is complete
    const [finalTouchSelection, setFinalTouchSelection] = useState<SelectedLineRange | null>(null);

    const { effectiveTheme } = useTheme();

    const patch = useMemo(() => convertHunksToPatch(hunks, filePath), [hunks, filePath]);

    // Keep refs in sync with state so useCallback handlers stay stable across renders.
    const pendingTouchSelectionRef = useRef(pendingTouchSelection);
    pendingTouchSelectionRef.current = pendingTouchSelection;
    const finalTouchSelectionRef = useRef(finalTouchSelection);
    finalTouchSelectionRef.current = finalTouchSelection;

    const createAnnotationFromRange = useCallback((range: SelectedLineRange) => {
        setCommentInputAnnotation({
            side: range.endSide ?? range.side ?? "additions",
            lineNumber: range.end,
            metadata: {
                startLine: range.start,
                endLine: range.end,
            },
        });
        setPendingTouchSelection(null);
    }, []);

    const handleLineSelectionStart = useCallback(
        (range: SelectedLineRange | null) => {
            if (range && pendingTouchSelectionRef.current && isTouchDevice()) {
                const combinedRange: SelectedLineRange = {
                    start: pendingTouchSelectionRef.current.start,
                    end: range.start,
                    side: pendingTouchSelectionRef.current.side,
                    endSide: range.side === pendingTouchSelectionRef.current.side ? undefined : range.side,
                };
                // normally, selection end triggers after selection start
                // but setting state somehow doesnt selection end
                // so need to create annotation from here
                setPendingTouchSelection(null);
                setFinalTouchSelection(combinedRange);
                createAnnotationFromRange(combinedRange);
            }
        },
        [createAnnotationFromRange],
    );

    const handleLineSelectionEnd = useCallback(
        (range: SelectedLineRange | null) => {
            // On touch devices: require two taps to create annotation
            // First tap records start line, second tap records end line
            if (isTouchDevice()) {
                if (finalTouchSelectionRef.current) {
                    createAnnotationFromRange(finalTouchSelectionRef.current);
                } else if (pendingTouchSelectionRef.current && !range) {
                    // Second tap on same line clears selection (range is null)
                    // Treat this as selecting the same line for start and end
                    createAnnotationFromRange(pendingTouchSelectionRef.current);
                } else if (range) {
                    // First tap: just highlight the line, wait for second tap
                    setPendingTouchSelection(range);
                }
            } else {
                // Desktop: create annotation immediately on selection end (drag release)
                if (range) {
                    createAnnotationFromRange(range);
                }
            }
        },
        [createAnnotationFromRange],
    );

    const handleCommentSubmit = (content: string) => {
        if (commentInputAnnotation?.metadata) {
            const { startLine, endLine } = commentInputAnnotation.metadata;
            const side = commentInputAnnotation.side;
            // Compute content hash for outdated detection
            const contentHash = computeContentHashForLines(hunks, startLine, endLine, side) ?? undefined;
            onAddComment?.(content, startLine, endLine, side, contentHash);
            setCommentInputAnnotation(null);
            setPendingTouchSelection(null);
            setFinalTouchSelection(null);
        }
    };

    const handleCommentCancel = () => {
        setCommentInputAnnotation(null);
        setPendingTouchSelection(null);
        setFinalTouchSelection(null);
    };

    // Create line annotations for comments and pending comment input
    const lineAnnotations = useMemo<DiffLineAnnotation<AnnotationMetadata>[]>(() => {
        const annotations: DiffLineAnnotation<AnnotationMetadata>[] = [];

        // map comments to line annotations
        if (comments) {
            for (const comment of comments) {
                annotations.push({
                    side: comment.side ?? "additions",
                    lineNumber: comment.endLine,
                    metadata: {
                        reviewComment: comment,
                        startLine: comment.startLine,
                        endLine: comment.endLine,
                    },
                });
            }
        }

        if (commentInputAnnotation) {
            annotations.push(commentInputAnnotation);
        }

        return annotations;
    }, [comments, commentInputAnnotation]);

    const handleCommentUpdate = (comment: ReviewComment) => (newContent: string) => {
        onUpdateComment?.(comment, newContent);
    };

    const handleCommentDelete = (comment: ReviewComment) => () => {
        onDeleteComment?.(comment);
    };

    const renderAnnotation = (annotation: DiffLineAnnotation<AnnotationMetadata>) => {
        const { startLine, endLine, reviewComment } = annotation.metadata;

        // Check if comment is outdated by comparing stored hash with current content hash
        let isOutdated = false;
        if (reviewComment?.contentHash) {
            const currentHash = computeContentHashForLines(
                hunks,
                startLine,
                endLine,
                reviewComment.side ?? "additions",
            );
            isOutdated = currentHash !== null && currentHash !== reviewComment.contentHash;
        }

        return (
            <InlineCommentAnnotation
                startLine={startLine}
                endLine={endLine}
                defaultComment={reviewComment?.content ?? ""}
                // when no review comment is associated with this annotation
                // it means this is a draft comment, so should be default editable
                defaultEditable={!reviewComment}
                isOutdated={isOutdated}
                author={reviewComment?.author}
                commentId={reviewComment?.id}
                filePath={filePath}
                thumbsUp={reviewComment?.thumbsUp}
                thumbsDown={reviewComment?.thumbsDown}
                onSubmit={reviewComment ? handleCommentUpdate(reviewComment) : handleCommentSubmit}
                onCancel={handleCommentCancel}
                onDelete={reviewComment ? handleCommentDelete(reviewComment) : undefined}
                onThumbsUp={
                    reviewComment && onSetCommentThumbsUp
                        ? (thumbsUp) => onSetCommentThumbsUp(reviewComment, thumbsUp)
                        : undefined
                }
                onThumbsDown={
                    reviewComment && onSetCommentThumbsDown
                        ? (thumbsDown) => onSetCommentThumbsDown(reviewComment, thumbsDown)
                        : undefined
                }
                onSendComment={reviewComment && onSendComment ? () => onSendComment(reviewComment) : undefined}
                onSendAllComments={onSendAllComments}
                allCommentsCount={allCommentsCount}
                isSendDisabled={isSendDisabled}
            />
        );
    };

    // Memoize options to prevent @pierre/diffs from detecting a "changed" config on every
    // render, which triggers forceRender → full DOM rebuild → scroll position reset.
    const enableLineSelection = !!onAddComment;
    const onLineSelectionEndStable = onAddComment ? handleLineSelectionEnd : undefined;
    const options = useMemo(
        () => ({
            theme: { dark: "ona-dark", light: "ona-light" } as const,
            themeType: effectiveTheme,
            diffStyle: "unified" as const,
            diffIndicators: "bars" as const,
            hunkSeparators: "simple" as const,
            expandUnchanged: expandUnchangedProp,
            disableFileHeader: true,
            overflow: "scroll" as const,
            enableLineSelection,
            enableHoverUtility: enableLineSelection,
            onLineSelectionStart: handleLineSelectionStart,
            onLineSelectionEnd: onLineSelectionEndStable,
            // Enable drag-to-select on mobile by preventing browser gesture handling
            unsafeCSS: `[data-interactive-line-numbers] [data-column-number] { touch-action: none; }`,
        }),
        [effectiveTheme, expandUnchangedProp, enableLineSelection, handleLineSelectionStart, onLineSelectionEndStable],
    );

    const renderHoverUtility = useMemo(
        () =>
            onAddComment
                ? () => (
                      <button
                          type="button"
                          className="size-5 rounded-md bg-surface-primary-inverted font-mono text-base text-content-primary-inverted"
                      >
                          +
                      </button>
                  )
                : undefined,
        [onAddComment],
    );

    const patchDiffStyle = useMemo(
        () =>
            ({
                "--diffs-font-family": "var(--font-family-mono)",
                "--diffs-gap-block": "0px",
            }) as React.CSSProperties,
        [],
    );

    if (!patch) {
        return null;
    }

    let selectedLines: SelectedLineRange | null | undefined;
    if (pendingTouchSelection) {
        selectedLines = pendingTouchSelection;
    } else if (finalTouchSelection) {
        selectedLines = finalTouchSelection;
    } else if (commentInputAnnotation) {
        selectedLines = undefined;
    } else {
        selectedLines = null;
    }

    return (
        <PatchDiff<AnnotationMetadata>
            patch={patch}
            options={options}
            // selectedLines logic:
            // - If there's a comment input annotation, let the component handle highlights (undefined)
            // - If there's a pending touch selection, show that selection highlighted
            // - Otherwise, clear all highlights (null) to ensure cancelling clears them
            selectedLines={selectedLines}
            lineAnnotations={lineAnnotations}
            renderAnnotation={renderAnnotation}
            renderHoverUtility={renderHoverUtility}
            style={patchDiffStyle}
        />
    );
};

export type ContentDiffViewProps = {
    originalContent: string;
    newContent: string;
    filePath?: string;
    /** Backend-provided hunks for determining which lines are changed */
    hunks: PlainGitHunk[];
    /** When true, shows all unchanged lines between hunks instead of collapsing them. */
    expandUnchanged?: boolean;
    comments?: ReviewComment[];
    onAddComment?: (
        content: string,
        startLine: number,
        endLine: number,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
    /** Send a single comment to Ona */
    onSendComment?: (reviewComment: ReviewComment) => void;
    /** Send all comments to Ona */
    onSendAllComments?: () => void;
    /** Total number of sendable comments */
    allCommentsCount?: number;
    /** Whether send actions are disabled */
    isSendDisabled?: boolean;
};

const ContentDiffView: FC<ContentDiffViewProps> = ({
    originalContent,
    newContent,
    filePath = "file",
    hunks,
    expandUnchanged: expandUnchangedProp = false,
    comments,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onSetCommentThumbsUp,
    onSetCommentThumbsDown,
    onSendComment,
    onSendAllComments,
    allCommentsCount,
    isSendDisabled,
}) => {
    const [commentInputAnnotation, setCommentInputAnnotation] = useState<DiffLineAnnotation<AnnotationMetadata> | null>(
        null,
    );

    // For touch devices: track pending selection that needs a second tap to confirm
    const [pendingTouchSelection, setPendingTouchSelection] = useState<SelectedLineRange | null>(null);
    const [finalTouchSelection, setFinalTouchSelection] = useState<SelectedLineRange | null>(null);

    const { effectiveTheme } = useTheme();

    const oldFile: FileContents = useMemo(
        () => ({
            name: filePath,
            contents: originalContent,
            cacheKey: `${filePath}:old:${originalContent.length}:${hashString(originalContent)}`,
        }),
        [filePath, originalContent],
    );

    const newFile: FileContents = useMemo(
        () => ({
            name: filePath,
            contents: newContent,
            cacheKey: `${filePath}:new:${newContent.length}:${hashString(newContent)}`,
        }),
        [filePath, newContent],
    );

    // Keep refs in sync with state so useCallback handlers stay stable across renders.
    const pendingTouchSelectionRef = useRef(pendingTouchSelection);
    pendingTouchSelectionRef.current = pendingTouchSelection;
    const finalTouchSelectionRef = useRef(finalTouchSelection);
    finalTouchSelectionRef.current = finalTouchSelection;

    const createAnnotationFromRange = useCallback((range: SelectedLineRange) => {
        setCommentInputAnnotation({
            side: range.endSide ?? range.side ?? "additions",
            lineNumber: range.end,
            metadata: {
                startLine: range.start,
                endLine: range.end,
            },
        });
        setPendingTouchSelection(null);
    }, []);

    const handleLineSelectionStart = useCallback(
        (range: SelectedLineRange | null) => {
            if (range && pendingTouchSelectionRef.current && isTouchDevice()) {
                const combinedRange: SelectedLineRange = {
                    start: pendingTouchSelectionRef.current.start,
                    end: range.start,
                    side: pendingTouchSelectionRef.current.side,
                    endSide: range.side === pendingTouchSelectionRef.current.side ? undefined : range.side,
                };
                setPendingTouchSelection(null);
                setFinalTouchSelection(combinedRange);
                createAnnotationFromRange(combinedRange);
            }
        },
        [createAnnotationFromRange],
    );

    const handleLineSelectionEnd = useCallback(
        (range: SelectedLineRange | null) => {
            if (isTouchDevice()) {
                if (finalTouchSelectionRef.current) {
                    createAnnotationFromRange(finalTouchSelectionRef.current);
                } else if (pendingTouchSelectionRef.current && !range) {
                    // Second tap on same line clears selection (range is null)
                    // Treat this as selecting the same line for start and end
                    createAnnotationFromRange(pendingTouchSelectionRef.current);
                } else if (range) {
                    // First tap: just highlight the line, wait for second tap
                    setPendingTouchSelection(range);
                }
            } else {
                // Desktop: create annotation immediately on selection end (drag release)
                if (range) {
                    createAnnotationFromRange(range);
                }
            }
        },
        [createAnnotationFromRange],
    );

    const handleCommentSubmit = (content: string) => {
        if (commentInputAnnotation?.metadata) {
            const { startLine, endLine } = commentInputAnnotation.metadata;
            const side = commentInputAnnotation.side;
            // Compute content hash for outdated detection
            // Use originalContent for deletions side, newContent for additions side
            const sourceContent = side === "deletions" ? originalContent : newContent;
            const contentHash = computeContentHash(sourceContent, startLine, endLine) ?? undefined;
            onAddComment?.(content, startLine, endLine, side, contentHash);
            setCommentInputAnnotation(null);
            setPendingTouchSelection(null);
            setFinalTouchSelection(null);
        }
    };

    const handleCommentCancel = () => {
        setCommentInputAnnotation(null);
        setPendingTouchSelection(null);
        setFinalTouchSelection(null);
    };

    // Create line annotations for comments and pending comment input
    const lineAnnotations = useMemo<DiffLineAnnotation<AnnotationMetadata>[]>(() => {
        const annotations: DiffLineAnnotation<AnnotationMetadata>[] = [];

        // map comments to line annotations
        if (comments) {
            for (const comment of comments) {
                annotations.push({
                    side: comment.side ?? "additions",
                    lineNumber: comment.endLine,
                    metadata: {
                        reviewComment: comment,
                        startLine: comment.startLine,
                        endLine: comment.endLine,
                    },
                });
            }
        }

        if (commentInputAnnotation) {
            annotations.push(commentInputAnnotation);
        }

        return annotations;
    }, [comments, commentInputAnnotation]);

    const handleCommentUpdate = (comment: ReviewComment) => (updatedContent: string) => {
        onUpdateComment?.(comment, updatedContent);
    };

    const handleCommentDelete = (comment: ReviewComment) => () => {
        onDeleteComment?.(comment);
    };

    const renderAnnotation = (annotation: DiffLineAnnotation<AnnotationMetadata>) => {
        const { startLine, endLine, reviewComment } = annotation.metadata;

        // Check if comment is outdated by comparing stored hash with current content hash
        // Use originalContent for deletions side, newContent for additions side
        let isOutdated = false;
        if (reviewComment?.contentHash) {
            const sourceContent = reviewComment.side === "deletions" ? originalContent : newContent;
            const currentHash = computeContentHash(sourceContent, startLine, endLine);
            isOutdated = currentHash !== null && currentHash !== reviewComment.contentHash;
        }

        return (
            <InlineCommentAnnotation
                startLine={startLine}
                endLine={endLine}
                defaultComment={reviewComment?.content ?? ""}
                defaultEditable={!reviewComment}
                isOutdated={isOutdated}
                author={reviewComment?.author}
                commentId={reviewComment?.id}
                filePath={filePath}
                thumbsUp={reviewComment?.thumbsUp}
                thumbsDown={reviewComment?.thumbsDown}
                onSubmit={reviewComment ? handleCommentUpdate(reviewComment) : handleCommentSubmit}
                onCancel={handleCommentCancel}
                onDelete={reviewComment ? handleCommentDelete(reviewComment) : undefined}
                onThumbsUp={
                    reviewComment && onSetCommentThumbsUp
                        ? (thumbsUp) => onSetCommentThumbsUp(reviewComment, thumbsUp)
                        : undefined
                }
                onThumbsDown={
                    reviewComment && onSetCommentThumbsDown
                        ? (thumbsDown) => onSetCommentThumbsDown(reviewComment, thumbsDown)
                        : undefined
                }
                onSendComment={reviewComment && onSendComment ? () => onSendComment(reviewComment) : undefined}
                onSendAllComments={onSendAllComments}
                allCommentsCount={allCommentsCount}
                isSendDisabled={isSendDisabled}
            />
        );
    };

    // Only expand unchanged sections if there are comments outside the diff hunks.
    // This avoids unnecessary expansion when all comments are on changed lines.
    const hasCommentsOutsideHunks = useMemo(() => {
        if (!comments || comments.length === 0) {
            return false;
        }

        const hunkRanges = hunks.map((h) => ({
            startLine: h.newStartLine,
            endLine: h.newStartLine + h.newLines - 1,
        }));

        return comments.some((comment) => {
            // Check if any line in the comment range is outside all hunks
            for (let line = comment.startLine; line <= comment.endLine; line++) {
                if (!isLineInHunk(line, hunkRanges)) {
                    return true;
                }
            }
            return false;
        });
    }, [comments, hunks]);

    const enableLineSelection = !!onAddComment;
    const onLineSelectionEndStable = onAddComment ? handleLineSelectionEnd : undefined;
    const options = useMemo(
        () => ({
            theme: { dark: "ona-dark", light: "ona-light" } as const,
            themeType: effectiveTheme,
            diffStyle: "unified" as const,
            diffIndicators: "bars" as const,
            hunkSeparators: "line-info" as const,
            expandUnchanged: expandUnchangedProp || hasCommentsOutsideHunks,
            disableFileHeader: true,
            overflow: "scroll" as const,
            enableLineSelection,
            enableHoverUtility: enableLineSelection,
            onLineSelectionStart: handleLineSelectionStart,
            onLineSelectionEnd: onLineSelectionEndStable,
            // Enable drag-to-select on mobile by preventing browser gesture handling
            unsafeCSS: `[data-interactive-line-numbers] [data-column-number] { touch-action: none; }`,
        }),
        [
            effectiveTheme,
            expandUnchangedProp,
            hasCommentsOutsideHunks,
            enableLineSelection,
            handleLineSelectionStart,
            onLineSelectionEndStable,
        ],
    );

    const renderHoverUtility = useMemo(
        () =>
            onAddComment
                ? () => (
                      <button
                          type="button"
                          className="size-5 rounded-md bg-surface-primary-inverted font-mono text-base text-content-primary-inverted"
                      >
                          +
                      </button>
                  )
                : undefined,
        [onAddComment],
    );

    const diffStyle = useMemo(
        () =>
            ({
                "--diffs-font-family": "var(--font-family-mono)",
            }) as React.CSSProperties,
        [],
    );

    let selectedLines: SelectedLineRange | null | undefined;
    if (pendingTouchSelection) {
        selectedLines = pendingTouchSelection;
    } else if (finalTouchSelection) {
        selectedLines = finalTouchSelection;
    } else if (commentInputAnnotation) {
        selectedLines = undefined;
    } else {
        selectedLines = null;
    }

    return (
        <MultiFileDiff<AnnotationMetadata>
            oldFile={oldFile}
            newFile={newFile}
            options={options}
            selectedLines={selectedLines}
            lineAnnotations={lineAnnotations}
            renderAnnotation={renderAnnotation}
            renderHoverUtility={renderHoverUtility}
            style={diffStyle}
        />
    );
};

export { GitDiffView, ContentDiffView };
